import { FC, useEffect } from 'react';

interface IExplicativonatalmetapapiProps {
  btnUrl: string;
}
const Explicativonatalmetapapi: FC<IExplicativonatalmetapapiProps> = ({ btnUrl }) => {
  useEffect(() => {
    // Function to handle smooth scrolling for anchor links
    const handleAnchorClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');

      if (anchor) {
        e.preventDefault();
        const hash = anchor.getAttribute('href');
        if (hash) {
          const targetElement = document.querySelector(hash);
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }
    };

    // Add click listener to document to handle dynamically added links
    document.addEventListener('click', handleAnchorClick);

    // Setup intersection observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          target.style.opacity = '1';
          target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    // Observe elements for animations
    const animatedElements = document.querySelectorAll('.vantaggio-card, .flow-item, .comparison-card');
    animatedElements.forEach((el) => {
      observer.observe(el);
    });

    // Cleanup
    return () => {
      document.removeEventListener('click', handleAnchorClick);
      observer.disconnect();
    };
  }, []);

  const htmlString = `<!DOCTYPE html>
  <html lang="pt-BR">
    <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>PAPI - 1 Spin. 3 Vantagens. Sistema Completo.</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;700&display=swap" rel="stylesheet">
        <style>
          :root {
          --gold: #FFD700;
          --gold-dark: #FFA500;
          --gold-light: #FFED4E;
          --black: #0A0A0A;
          --dark-gray: #1A1A1A;
          --medium-gray: #2A2A2A;
          --red: #FF3333;
          --green: #00FF88;
          --white: #FFFFFF;
        }

          * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

          body {
          font-family: 'DM Sans', sans-serif;
          background: var(--black);
          color: var(--white);
          overflow-x: hidden;
          line-height: 1.6;
        }

          .bg-animation {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          opacity: 0.15;
          background:
          radial-gradient(circle at 20% 50%, var(--gold) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, var(--red) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, var(--gold-dark) 0%, transparent 50%);
          animation: pulse 8s ease-in-out infinite;
        }

          @keyframes pulse {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.25; }
        }

          header {
          position: relative;
          z-index: 10;
          padding: 2rem 5%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(255, 215, 0, 0.2);
        }

          .logo {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 3rem;
          color: var(--gold);
          text-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
          letter-spacing: 0.1em;
        }

          .cta-button {
          padding: 1rem 2.5rem;
          background: linear-gradient(135deg, var(--gold), var(--gold-dark));
          color: var(--black);
          font-weight: 700;
          font-size: 1.1rem;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 5px 25px rgba(255, 215, 0, 0.4);
        }

          .cta-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 35px rgba(255, 215, 0, 0.6);
        }

          .hero {
          position: relative;
          z-index: 1;
          padding: 8rem 5% 6rem;
          text-align: center;
        }

          .hero-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(3rem, 8vw, 7rem);
          line-height: 0.9;
          margin-bottom: 1.5rem;
          color: var(--gold);
          text-shadow: 0 0 40px rgba(255, 215, 0, 0.4);
          animation: fadeInUp 1s ease;
        }

          .hero-subtitle {
          font-size: clamp(1.3rem, 3vw, 2rem);
          margin-bottom: 1rem;
          color: var(--white);
          font-weight: 500;
          animation: fadeInUp 1s ease 0.2s backwards;
        }

          .hero-description {
          font-size: 1.2rem;
          max-width: 700px;
          margin: 0 auto 3rem;
          color: rgba(255, 255, 255, 0.8);
          animation: fadeInUp 1s ease 0.4s backwards;
        }

          @keyframes fadeInUp {
          from {
          opacity: 0;
          transform: translateY(30px);
        }
          to {
          opacity: 1;
          transform: translateY(0);
        }
        }

          .vantaggi-section {
          position: relative;
          z-index: 1;
          padding: 4rem 5%;
          max-width: 1400px;
          margin: 0 auto;
        }

          .section-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(2.5rem, 5vw, 4.5rem);
          text-align: center;
          margin-bottom: 1rem;
          color: var(--gold);
        }

          .section-subtitle {
          text-align: center;
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 5rem;
        }

          .vantaggi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 3rem;
          margin-bottom: 4rem;
        }

          .vantaggio-card {
          background: linear-gradient(135deg, var(--dark-gray), var(--medium-gray));
          border: 2px solid transparent;
          border-radius: 20px;
          padding: 3rem 2rem;
          position: relative;
          overflow: hidden;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          animation: fadeInUp 0.8s ease backwards;
        }

          .vantaggio-card:nth-child(1) { animation-delay: 0.1s; }
          .vantaggio-card:nth-child(2) { animation-delay: 0.2s; }
          .vantaggio-card:nth-child(3) { animation-delay: 0.3s; }

          .vantaggio-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, var(--gold), var(--gold-dark));
          opacity: 0;
          transition: opacity 0.5s ease;
          z-index: 0;
        }

          .vantaggio-card:hover {
          transform: translateY(-10px);
          border-color: var(--gold);
          box-shadow: 0 20px 60px rgba(255, 215, 0, 0.3);
        }

          .vantaggio-card:hover::before {
          opacity: 0.1;
        }

          .vantaggio-content {
          position: relative;
          z-index: 1;
        }

          .vantaggio-number {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 5rem;
          color: var(--gold);
          line-height: 1;
          margin-bottom: 1rem;
          text-shadow: 0 0 20px rgba(255, 215, 0, 0.4);
        }

          .vantaggio-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: var(--white);
        }

          .vantaggio-subtitle {
          font-size: 1.1rem;
          color: var(--gold);
          margin-bottom: 1.5rem;
          font-weight: 600;
        }

          .vantaggio-amount {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--green);
          margin-bottom: 1rem;
        }

          .vantaggio-description {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.7;
        }

          .vantaggio-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          background: rgba(255, 215, 0, 0.2);
          border: 1px solid var(--gold);
          border-radius: 20px;
          font-size: 0.9rem;
          color: var(--gold);
          margin-top: 1rem;
          font-weight: 600;
        }

          .flow-section {
          position: relative;
          z-index: 1;
          padding: 6rem 5%;
          background: linear-gradient(180deg, transparent, var(--dark-gray), transparent);
        }

          .flow-container {
          max-width: 1000px;
          margin: 0 auto;
          text-align: center;
        }

          .flow-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(2rem, 4vw, 3.5rem);
          margin-bottom: 4rem;
          color: var(--white);
        }

          .flow-visual {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          flex-wrap: wrap;
          margin-bottom: 3rem;
        }

          .flow-item {
          flex: 1;
          min-width: 200px;
          padding: 2rem;
          background: var(--medium-gray);
          border: 2px solid var(--gold);
          border-radius: 15px;
          position: relative;
          animation: pulse-border 2s ease-in-out infinite;
        }

          .flow-item:nth-child(1) { animation-delay: 0s; }
          .flow-item:nth-child(2) { animation-delay: 0.3s; }
          .flow-item:nth-child(3) { animation-delay: 0.6s; }

          @keyframes pulse-border {
          0%, 100% { border-color: var(--gold); box-shadow: 0 0 15px rgba(255, 215, 0, 0.3); }
          50% { border-color: var(--gold-light); box-shadow: 0 0 30px rgba(255, 215, 0, 0.6); }
        }

          .flow-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

          .flow-label {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--gold);
        }

          .flow-arrow {
          font-size: 2rem;
          color: var(--gold);
          animation: slide 1.5s ease-in-out infinite;
        }

          @keyframes slide {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(10px); }
        }

          .comparison-section {
          position: relative;
          z-index: 1;
          padding: 6rem 5%;
          max-width: 1200px;
          margin: 0 auto;
        }

          .comparison-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(2.5rem, 5vw, 4rem);
          text-align: center;
          margin-bottom: 4rem;
          color: var(--gold);
        }

          .comparison-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
        }

          .comparison-card {
          padding: 3rem;
          border-radius: 20px;
          position: relative;
          overflow: hidden;
        }

          .comparison-card.others {
          background: linear-gradient(135deg, #2A2A2A, #1A1A1A);
          border: 2px solid #444;
        }

          .comparison-card.papi {
          background: linear-gradient(135deg, var(--medium-gray), var(--dark-gray));
          border: 3px solid var(--gold);
          box-shadow: 0 10px 40px rgba(255, 215, 0, 0.3);
        }

          .comparison-label {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 2rem;
          margin-bottom: 2rem;
        }

          .comparison-card.others .comparison-label {
          color: #888;
        }

          .comparison-card.papi .comparison-label {
          color: var(--gold);
        }

          .comparison-list {
          list-style: none;
        }

          .comparison-list li {
          padding: 1rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          gap: 1rem;
          font-size: 1.1rem;
        }

          .comparison-list li:last-child {
          border-bottom: none;
        }

          .check {
          color: var(--green);
          font-size: 1.5rem;
        }

          .cross {
          color: var(--red);
          font-size: 1.5rem;
        }

          .cta-section {
          position: relative;
          z-index: 1;
          padding: 8rem 5%;
          text-align: center;
          background: linear-gradient(135deg, var(--dark-gray), var(--black));
        }

          .cta-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(3rem, 6vw, 5rem);
          margin-bottom: 2rem;
          color: var(--gold);
          line-height: 1;
        }

          .cta-subtitle {
          font-size: 1.5rem;
          margin-bottom: 3rem;
          color: rgba(255, 255, 255, 0.8);
        }

          .cta-button-large {
          display: inline-block;
          padding: 1.5rem 4rem;
          background: linear-gradient(135deg, var(--gold), var(--gold-dark));
          color: var(--black);
          font-weight: 700;
          font-size: 1.5rem;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 10px 40px rgba(255, 215, 0, 0.4);
          font-family: 'Bebas Neue', sans-serif;
          letter-spacing: 0.05em;
        }

          .cta-button-large:hover {
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 15px 50px rgba(255, 215, 0, 0.6);
        }

          footer {
          position: relative;
          z-index: 1;
          padding: 2rem 5%;
          border-top: 1px solid rgba(255, 215, 0, 0.2);
          text-align: center;
          color: rgba(255, 255, 255, 0.5);
        }

          @media (max-width: 768px) {
          .comparison-grid {
          grid-template-columns: 1fr;
        }

          .flow-visual {
          flex-direction: column;
        }

          .flow-arrow {
          transform: rotate(90deg);
        }

          header {
          flex-direction: column;
          gap: 1.5rem;
        }
        }
        </style>
      </head>
      <body>
      <div class="bg-animation"></div>

      <header>
        <div class="logo">PAPI.BET</div>
        <a 
          href="${btnUrl}"  
          class="cta-button" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          CADASTRE-SE
        </a>
      </header>

      <section class="hero">
        <h1 class="hero-title">1 SPIN. 3 VANTAGENS.</h1>
        <p class="hero-subtitle">Um sistema de 3 níveis que premia sempre.</p>
        <p class="hero-description">
          No PAPI cada jogada faz três coisas: te premia hoje, te faz avançar na escalada semanal,
          e se perder te devolve até 10%.
        </p>
        <a href="#vantaggi" class="cta-button">DESCUBRA COMO FUNCIONA</a>
      </section>

      <section id="vantaggi" class="vantaggi-section">
        <h2 class="section-title">O Sistema de 3 Níveis</h2>
        <p class="section-subtitle">Cada spin no PAPI trabalha para você de três maneiras diferentes</p>

        <div class="vantaggi-grid">
          <div class="vantaggio-card">
            <div class="vantaggio-content">
              <div class="vantaggio-number">1️⃣</div>
              <h3 class="vantaggio-title">Desafio Diário</h3>
              <p class="vantaggio-subtitle">Sua recompensa de hoje</p>
              <div class="vantaggio-amount">Até R$50.000</div>
              <p class="vantaggio-description">
                Todo dia você acumula pontos e desbloqueia recompensas imediatas.
                Não espera o fim da semana ou do mês.
              </p>
              <span class="vantaggio-badge">Joga hoje, ganha hoje</span>
            </div>
          </div>

          <div class="vantaggio-card">
            <div class="vantaggio-content">
              <div class="vantaggio-number">2️⃣</div>
              <h3 class="vantaggio-title">Desafio Semanal</h3>
              <p class="vantaggio-subtitle">Sua escalada para o topo</p>
              <div class="vantaggio-amount">Até R$500.000</div>
              <p class="vantaggio-description">
                Cada spin de cada dia contribui para seu progresso semanal.
                15 níveis para desbloquear com prêmios crescentes até R$ 500.000 totais.
              </p>
              <span class="vantaggio-badge">Quanto mais joga, mais sobe</span>
            </div>
          </div>

          <div class="vantaggio-card">
            <div class="vantaggio-content">
              <div class="vantaggio-number">3️⃣</div>
              <h3 class="vantaggio-title">Cashback</h3>
              <p class="vantaggio-subtitle">Sua proteção</p>
              <div class="vantaggio-amount">Até 10%</div>
              <p class="vantaggio-description">
                Foi azarado? Toda semana você recupera até 10% das perdas líquidas.
                Não é o prêmio principal, é sua rede de segurança.
              </p>
              <span class="vantaggio-badge">Se perder, é reembolsado</span>
            </div>
          </div>
        </div>
      </section>

      <section class="flow-section">
        <div class="flow-container">
          <h2 class="flow-title">Como Funciona na Prática</h2>
          <div class="flow-visual">
            <div class="flow-item">
              <div class="flow-icon">🎰</div>
              <div class="flow-label">Cada Spin</div>
            </div>
            <div class="flow-arrow">→</div>
            <div class="flow-item">
              <div class="flow-icon">📊</div>
              <div class="flow-label">Acumula Pontos</div>
            </div>
            <div class="flow-arrow">→</div>
            <div class="flow-item">
              <div class="flow-icon">🏆</div>
              <div class="flow-label">3 Recompensas</div>
            </div>
          </div>
          <p style="font-size: 1.3rem; color: var(--gold); font-weight: 600; margin-top: 2rem;">
            No PAPI você não joga apenas para ganhar. <br>Joga para acumular.
          </p>
        </div>
      </section>

      <section class="comparison-section">
        <h2 class="comparison-title">PAPI vs Outros Cassinos</h2>
        <div class="comparison-grid">
          <div class="comparison-card others">
            <h3 class="comparison-label">Outros Cassinos</h3>
            <ul class="comparison-list">
              <li>
                <span class="check">✓</span>
                <span>Cashback</span>
              </li>
              <li>
                <span class="cross">✗</span>
                <span>Nenhuma recompensa diária</span>
              </li>
              <li>
                <span class="cross">✗</span>
                <span>Nenhuma escalada progressiva</span>
              </li>
              <li>
                <span class="cross">✗</span>
                <span>Uma única vantagem</span>
              </li>
            </ul>
          </div>

          <div class="comparison-card papi">
            <h3 class="comparison-label">Sistema PAPI</h3>
            <ul class="comparison-list">
              <li>
                <span class="check">✓</span>
                <span>Cashback até 10%</span>
              </li>
              <li>
                <span class="check">✓</span>
                <span>Prêmios diários até R$50k</span>
              </li>
              <li>
                <span class="check">✓</span>
                <span>Escalada semanal até R$500k</span>
              </li>
              <li>
                <span class="check">✓</span>
                <span>Acumula sempre, ganha de qualquer jeito</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section class="cta-section">
        <h2 class="cta-title">Pronto para Jogar Smart?</h2>
        <p class="cta-subtitle">Junte-se ao PAPI e descubra a verdadeira vantagem do jogador</p>
        <a 
          href="${btnUrl}"  
          class="cta-button-large" 
          target="_blank" 
          rel="noopener noreferrer"
        >COMECE AGORA</a>
      </section>

      <footer>
        <p>&copy; 2024 PAPI.BET - Jogo responsável 18+</p>
      </footer>
      </body>
    </html>`;
  return <div dangerouslySetInnerHTML={{ __html: htmlString }} />;
};

export default Explicativonatalmetapapi;
